import React from 'react';
import { colors, typography } from './tokens';

export type DocumentStatus = 'complete' | 'missing' | 'pending' | 'draft';
export type DocumentType = 'invoice' | 'bill-of-lading' | 'certificate' | 'packing-list' | 'other';

export interface DocumentCardProps {
  title: string;
  date?: string;
  status: DocumentStatus;
  type?: DocumentType;
  fileSize?: string;
  onClick?: () => void;
  className?: string;
}

const statusStyles: Record<DocumentStatus, {
  borderColor: string;
  backgroundColor: string;
  iconColor: string;
  textColor: string;
}> = {
  complete: {
    borderColor: colors.status.positive,
    backgroundColor: '#F6FFF8',
    iconColor: colors.status.positive,
    textColor: colors.text.primary,
  },
  missing: {
    borderColor: colors.status.negative,
    backgroundColor: '#FFF6F6',
    iconColor: colors.status.negative,
    textColor: colors.status.negative,
  },
  pending: {
    borderColor: colors.status.neutral,
    backgroundColor: colors.background.secondary,
    iconColor: colors.text.tertiary,
    textColor: colors.text.secondary,
  },
  draft: {
    borderColor: colors.status.warning,
    backgroundColor: '#FFFBF0',
    iconColor: colors.status.warning,
    textColor: colors.text.primary,
  },
};


export const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  date,
  status,
  type = 'other',
  fileSize,
  onClick,
  className = '',
}) => {
  const styles = statusStyles[status];
  const isClickable = onClick !== undefined;
  
  return (
    <div
      className={`${className} ${isClickable ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      style={{
        backgroundColor: styles.backgroundColor,
        border: `1px solid ${styles.borderColor}`,
        borderRadius: '6px',
        padding: '8px',
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '6px',
        transition: 'all 0.2s ease',
        position: 'relative',
        ...(isClickable && {
          ':hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transform: 'translateY(-1px)',
          }
        })
      }}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        if (isClickable) {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'none';
        }
      }}
    >
      {/* Header with file size */}
      {fileSize && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '2px' }}>
          <span
            style={{
              fontSize: '9px',
              color: colors.text.tertiary,
              fontFamily: typography.fontFamily.primary,
              fontWeight: typography.fontWeight.regular,
            }}
          >
            {fileSize}
          </span>
        </div>
      )}
      
      {/* Document title */}
      <div>
        <h4
          style={{
            fontSize: '11px',
            fontWeight: typography.fontWeight.bold,
            fontFamily: typography.fontFamily.redditSans,
            color: styles.textColor,
            margin: 0,
            lineHeight: 1.2,
            textTransform: 'uppercase',
            letterSpacing: '0.22px',
          }}
        >
          {title}
        </h4>
      </div>
      
      {/* Date */}
      {date && (
        <div
          style={{
            fontSize: '10px',
            fontWeight: typography.fontWeight.regular,
            fontFamily: typography.fontFamily.redditSans,
            color: colors.text.secondary,
            margin: 0,
            lineHeight: 1,
          }}
        >
          {date}
        </div>
      )}
    </div>
  );
};

// Status indicator variants for convenience
export const CompleteDocumentCard: React.FC<Omit<DocumentCardProps, 'status'>> = (props) => (
  <DocumentCard {...props} status="complete" />
);

export const MissingDocumentCard: React.FC<Omit<DocumentCardProps, 'status'>> = (props) => (
  <DocumentCard {...props} status="missing" />
);

export const PendingDocumentCard: React.FC<Omit<DocumentCardProps, 'status'>> = (props) => (
  <DocumentCard {...props} status="pending" />
);

export const DraftDocumentCard: React.FC<Omit<DocumentCardProps, 'status'>> = (props) => (
  <DocumentCard {...props} status="draft" />
);